import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Lead, LeadPatch } from '@shared/types';
import { formatDateTime } from '@shared/dates';
import { telLink, waLink } from '@shared/phone';
import { SOURCE_OPTIONS } from '@shared/sources';
import { useLeadDetail, useLeadTasks, usePatchLead, useSetOwner, useStages, useToast } from '../store';
import { Empty, Icons, Modal, Spinner, useEscape, useIsMobile } from './ui';
import { StagePill } from './StagePill';
import { NoteComposer, NotesTimeline } from './Notes';
import { LeadTasks, NextTask } from './Tasks';

/** Right drawer on desktop, full-screen sheet on mobile. Same sections, same order. */
export function LeadDetailPanel({ leadId, onClose }: { leadId: string | null; onClose: () => void }) {
  const mobile = useIsMobile();
  useEscape(onClose);
  useEffect(() => {
    if (!leadId) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [leadId]);
  if (!leadId) return null;
  const body = <LeadDetailBody leadId={leadId} onClose={onClose} />;
  return createPortal(
    mobile ? (
      <div className="fixed inset-0 z-40 flex flex-col bg-panel sheet-enter" role="dialog" aria-modal="true">{body}</div>
    ) : (
      <div className="fixed inset-0 z-40 bg-black/30" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <aside className="drawer-enter absolute inset-y-0 start-0 flex w-full max-w-xl flex-col bg-panel shadow-2xl" role="dialog" aria-modal="true">{body}</aside>
      </div>
    ),
    document.body,
  );
}

function LeadDetailBody({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const { data, isLoading, error } = useLeadDetail(leadId);
  const { userName, meta, stageName } = useStages();
  const [tab, setTab] = useState<'details' | 'tasks' | 'activity'>('details');
  const tasks = useLeadTasks(leadId);
  const [editing, setEditing] = useState(false);
  const setOwner = useSetOwner();
  const toast = useToast();

  if (isLoading) return <div className="flex flex-1 items-center justify-center"><Spinner /></div>;
  if (error || !data) return <div className="p-4"><button className="btn" onClick={onClose}>סגור</button><p className="mt-4 text-danger">הליד לא נמצא</p></div>;
  const lead = data;
  const copy = (v: string | null) => { if (!v) return; void navigator.clipboard?.writeText(v); toast('הועתק'); };

  return (
    <>
      <header className="flex items-start gap-2 border-b border-line p-4">
        <button className="btn btn-icon" aria-label="סגור" onClick={onClose}>✕</button>
        <div className="min-w-0 flex-1">
          <h2 className="flex min-w-0 text-lg font-bold"><span dir="auto" className="min-w-0 truncate">{lead.fullName || <Empty />}</span></h2>
          <div className="mt-1 flex items-center gap-2"><StagePill lead={lead} size="md" /><span className="text-xs text-ink-3">{meta?.pipelines.find((p) => p.id === lead.pipelineId)?.name}</span></div>
        </div>
        <button className="btn btn-icon" aria-label="עריכת ליד" title="עריכת ליד" onClick={() => setEditing(true)}><Icons.Edit /></button>
      </header>

      <nav className="flex border-b border-line px-4" role="tablist">
        {(['details', 'tasks', 'activity'] as const).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === t ? 'border-accent font-semibold text-accent' : 'border-transparent text-ink-2'}`} onClick={() => setTab(t)}>
            {t === 'details' ? 'פרטי ליד' : t === 'tasks' ? `משימות (${lead.openTaskCount})` : 'יומן פעילות'}
          </button>
        ))}
      </nav>

      <div className="min-h-0 flex-1 overflow-y-auto p-4" style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}>
        {tab === 'tasks' ? <LeadTasks leadId={lead.id} tasks={tasks.data} loading={tasks.isLoading} /> : tab === 'details' ? (
          <div className="grid gap-5">
            <NextTask tasks={tasks.data} onShowAll={() => setTab('tasks')} />
            <section className="card grid gap-2 p-3 text-sm">
              <Row label="טלפון">
                {lead.phone ? (
                  <span className="flex items-center gap-1">
                    <a className="ltr font-medium text-accent" href={telLink(lead.phone) ?? '#'}>{lead.phone}</a>
                    <button className="btn btn-icon btn-sm" title="העתק" aria-label="העתק טלפון" onClick={() => copy(lead.phone)}><Icons.Copy size={16} /></button>
                    {waLink(lead.phone) && <a className="btn btn-icon btn-sm text-green-700" title="WhatsApp" aria-label="WhatsApp" href={waLink(lead.phone, meta?.tenant.settings.whatsappMessage)!} target="_blank" rel="noreferrer"><Icons.WhatsApp size={16} /></a>}
                    <a className="btn btn-icon btn-sm" title="התקשר" aria-label="התקשר" href={telLink(lead.phone) ?? '#'}><Icons.Phone size={16} /></a>
                  </span>
                ) : <Empty />}
              </Row>
              <Row label="אימייל">{lead.email ? <span className="flex items-center gap-1"><a className="ltr text-accent" href={`mailto:${lead.email}`}>{lead.email}</a><button className="btn btn-icon btn-sm" aria-label="העתק אימייל" onClick={() => copy(lead.email)}><Icons.Copy size={16} /></button></span> : <Empty />}</Row>
              <Row label="מקור הליד">{lead.source ?? <Empty />}</Row>
              <Row label="ערוץ">{lead.medium ?? <Empty />}</Row>
              <Row label="תאריך ליד"><span className="ltr">{formatDateTime(lead.leadDate)}</span></Row>
              <Row label="נוצר ב-CRM"><span className="ltr">{formatDateTime(lead.createdAt)}</span></Row>
              <Row label="אחראי">
                <select className="input" value={lead.assignedTo ?? ''} onChange={(e) => setOwner.mutate({ id: lead.id, assignedTo: e.target.value || null })} aria-label="אחראי">
                  <option value="">— ללא —</option>
                  {meta?.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </Row>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold">הערות</h3>
              <div className="card p-3"><NoteComposer leadId={lead.id} /></div>
              <div className="mt-3"><NotesTimeline notes={lead.notes} leadId={lead.id} /></div>
            </section>

            {Object.keys(lead.attribution).length > 0 && (
              <section>
                <h3 className="mb-2 text-sm font-semibold">שיוך (UTM)</h3>
                <div className="card grid gap-1 p-3 text-xs">
                  {attributionEntries(lead.attribution).map(([k, v]) => <div key={k} className="flex gap-2"><span className="w-28 shrink-0 text-ink-3">{k}</span><span className="ltr break-all">{v}</span></div>)}
                </div>
              </section>
            )}

            {lead.opportunities.length > 1 && (
              <section>
                <h3 className="mb-2 text-sm font-semibold">ליד חוזר · {lead.opportunities.length} פניות</h3>
                <ol className="card grid gap-1 p-3 text-xs">
                  {lead.opportunities.map((o) => (
                    <li key={o.id} className={`grid grid-cols-[110px_1fr_auto] gap-2 ${o.id === lead.opportunityId ? 'font-semibold' : ''}`}>
                      <span className="ltr text-ink-3">{formatDateTime(o.createdAt)}</span>
                      <span className="min-w-0 truncate" dir="auto">{o.source ?? '—'}</span>
                      <span className="text-ink-3">{stageName(o.stageId)}</span>
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </div>
        ) : (
          <ActivityLog lead={lead} userName={userName} />
        )}
      </div>

      <EditLeadModal open={editing} lead={lead} onClose={() => setEditing(false)} />
    </>
  );
}

/** Attribution fields in reading order: where from, which ad, then click id and raw URLs; anything else last. */
const ATTRIBUTION_ORDER = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'placement', 'utm_id', '_atid', 'referrer', 'source_url'];
function attributionEntries(a: Record<string, string>): Array<[string, string]> {
  const rank = (k: string) => { const i = ATTRIBUTION_ORDER.indexOf(k); return i === -1 ? ATTRIBUTION_ORDER.length : i; };
  return Object.entries(a).sort(([x], [y]) => rank(x) - rank(y) || x.localeCompare(y));
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="grid grid-cols-[110px_1fr] items-center gap-2"><span className="text-ink-3">{label}</span><span className="min-w-0 truncate">{children}</span></div>;
}

function ActivityLog({ lead, userName }: { lead: Lead & { notes: { id: string; body: string; authorUserId: string | null; createdAt: string | null; kind: string }[]; createdAt: string | null }; userName: (id: string | null) => string }) {
  const { stageName } = useStages();
  const items = [
    ...(lead.createdAt ? [{ at: lead.createdAt, text: 'הליד נוצר ב-CRM' }] : []),
    ...(lead.leadDate && lead.leadDate !== lead.createdAt ? [{ at: lead.leadDate, text: 'תאריך הליד' }] : []),
    ...lead.notes.filter((n) => n.kind === 'comment').map((n) => ({ at: n.createdAt ?? '', text: `${userName(n.authorUserId)}: ${n.body}` })),
    ...(lead.updatedAt ? [{ at: lead.updatedAt, text: `עדכון אחרון · סטטוס ${stageName(lead.stageId)}` }] : []),
  ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  return (
    <ol className="grid gap-2">
      {items.map((it, i) => (
        <li key={i} className="grid grid-cols-[120px_1fr] gap-2 text-sm"><span className="ltr text-xs text-ink-3">{formatDateTime(it.at)}</span><span className="whitespace-pre-wrap break-words">{it.text}</span></li>
      ))}
    </ol>
  );
}

/** עריכת ליד: name (single field), phone, email, source, status. Lead date and notes are never editable. */
export function EditLeadModal({ open, lead, onClose }: { open: boolean; lead: Lead; onClose: () => void }) {
  const { meta } = useStages();
  const patch = usePatchLead();
  const [form, setForm] = useState({ fullName: lead.fullName, phone: lead.phone ?? '', email: lead.email ?? '', source: lead.source ?? '' });
  useEffect(() => { setForm({ fullName: lead.fullName, phone: lead.phone ?? '', email: lead.email ?? '', source: lead.source ?? '' }); }, [lead, open]);
  const sources = Array.from(new Set([...SOURCE_OPTIONS, form.source].filter(Boolean)));
  const save = () => {
    const p: LeadPatch = {};
    if (form.fullName.trim() !== lead.fullName) p.fullName = form.fullName;
    if (form.phone.trim() !== (lead.phone ?? '')) p.phone = form.phone.trim() || null;
    if (form.email.trim() !== (lead.email ?? '')) p.email = form.email.trim() || null;
    if (form.source !== (lead.source ?? '')) p.source = form.source || null;
    if (Object.keys(p).length === 0) return onClose();
    patch.mutate({ id: lead.id, patch: p }, { onSuccess: onClose });
  };
  return (
    <Modal open={open} onClose={onClose} title="עריכת ליד" footer={<><button className="btn" onClick={onClose}>ביטול</button><button className="btn btn-primary" onClick={save} disabled={patch.isPending || !form.fullName.trim()}>{patch.isPending ? <Spinner /> : 'שמור שינויים'}</button></>}>
      <form className="grid gap-3" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <label className="grid gap-1 text-sm"><span>שם מלא</span><input className="input" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} required /></label>
        <label className="grid gap-1 text-sm"><span>טלפון</span><input className="input ltr" dir="ltr" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
        <label className="grid gap-1 text-sm"><span>אימייל</span><input className="input ltr" dir="ltr" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label className="grid gap-1 text-sm"><span>מקור הליד</span>
          <input className="input" list="sources" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
          <datalist id="sources">{sources.map((s) => <option key={s} value={s} />)}</datalist>
        </label>
        <div className="grid gap-1 text-sm"><span>סטטוס</span><div><StagePill lead={lead} size="md" /></div></div>
        <p className="text-xs text-ink-3">תאריך הליד וההערות נגזרים מ-CRM ואינם ניתנים לעריכה. הפייפליין: {meta?.pipelines.find((p) => p.id === lead.pipelineId)?.name}</p>
      </form>
    </Modal>
  );
}
